import { Component, inject, Inject, Optional, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { CommonModule } from '@angular/common';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { emailFormatValidator, phoneValidator } from '@core/shared/form-validators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrServices } from '@services/toastr/toastr.service';
import { Prefix} from '@interfaces/prefix';
import {SettingsApiService} from '@services/api/settings/settings-api.service';
import { CompaniesApiService} from '@services/api/companies/companies-api.service';
import { Subject, takeUntil, finalize} from 'rxjs';
import { Company} from '@interfaces/company';
import { Roles, RoleLabelKeys } from '@enums/Roles';
import { TechnicianApiService } from '@services/api/user/technician/technician-api.service';
import { Technician } from '@interfaces/technician';
import {AuthService} from '@services/api/auth/auth.service';
import {User} from '@interfaces/user';
import {ModalsConfirmComponent} from '@components/modals/modals-confirm/modals-confirm.component';

@Component({
  selector: 'app-profile-technician',
  imports: [MaterialModule, CommonModule, ReactiveFormsModule, TablerIconsModule, TranslateModule, FormsModule],
  templateUrl: './profile.component.html',
  standalone: true,
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit, OnDestroy {
  technicianForm: FormGroup;
  public dialog = inject(MatDialog);
  protected companies: Company[] = [];
  protected companyFilter: string = '';
  private prefix_api_service = inject(SettingsApiService);
  private company_api_service = inject(CompaniesApiService);
  private technician_api_service = inject(TechnicianApiService);
  protected prefixes: Prefix[] = [];
  protected prefixFilter: string = '';
  private destroy$ = new Subject<void>();
  private user_api_service = inject(AuthService);
  private toastr_service = inject(ToastrServices);
  private translate = inject(TranslateService);
  saving = false;
  protected user: User | null = null;
  protected readonly: boolean = false;

  @ViewChild('filterInput') filterInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('companyFilterInput') companyFilterInputRef!: ElementRef<HTMLInputElement>;
  readonly RoleLabelKeys = RoleLabelKeys;

  constructor(
    public dialogRef: MatDialogRef<ProfileComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: Technician | null,
    private fb: FormBuilder)
  {
    this.technicianForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, emailFormatValidator]],
      name: ['', [Validators.required]],
      lastname: ['', [Validators.required]],
      // Field technician by default; the superadmin can also create the backoffice of a new company
      role: [Roles.TECHNICIAN as number, [Validators.required]],
      company: ['', [Validators.required]],
      new_company_name: [''],
      new_company_vat: [''],
      prefix: [''],
      phone: ['', phoneValidator()],
    });
  }

  ngOnInit(): void {
    this.getUserData();
    this.loadPrefixes();
    this.loadCompanies();
    this.populateFormOnEdit();
    this.technicianForm.get('role')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateCompanyValidators());
  }

  get isSuperadmin(): boolean {
    return this.user?.roles?.id === Roles.SUPER_ADMIN;
  }

  get isBackofficeUser(): boolean {
    return this.user?.roles?.id === Roles.MANAGEMENT_SYSTEM;
  }

  // Types the logged user may give: the backoffice only creates technicians of its own company
  get roleOptions(): number[] {
    if (this.data?.roles?.id === Roles.MANAGEMENT_SYSTEM) {
      return [Roles.MANAGEMENT_SYSTEM];
    }
    const options = [Roles.TECHNICIAN, Roles.OPERATOR_BO];
    if (this.isSuperadmin && !this.data) {
      options.push(Roles.MANAGEMENT_SYSTEM);
    }
    return options;
  }

  // A new backoffice comes with its company: name and VAT number instead of choosing one
  get isNewCompany(): boolean {
    return !this.data && this.technicianForm.get('role')?.value === Roles.MANAGEMENT_SYSTEM;
  }

  // The backoffice works on its own company, the API decides it: no company field for it
  get showCompanySelect(): boolean {
    return !this.isNewCompany && !this.isBackofficeUser;
  }

  // The API refuses a VAT number already used by a company, but the panel turns every 400 into a generic message:
  // checked here against the companies the superadmin already sees, so the reason is shown before saving
  private vatNotTaken = (control: AbstractControl): ValidationErrors | null => {
    const vat = String(control.value ?? '').trim().toLowerCase();
    const taken = !!vat && this.companies.some(company => String(company.vat_number ?? '').trim().toLowerCase() === vat);
    return taken ? { vatTaken: true } : null;
  };

  private updateCompanyValidators(): void {
    const company = this.technicianForm.get('company');
    const name = this.technicianForm.get('new_company_name');
    const vat = this.technicianForm.get('new_company_vat');
    company?.setValidators(this.showCompanySelect ? [Validators.required] : []);
    name?.setValidators(this.isNewCompany ? [Validators.required] : []);
    vat?.setValidators(this.isNewCompany ? [Validators.required, this.vatNotTaken] : []);
    if (this.isBackofficeUser && !company?.value && this.user?.company?.id) {
      company?.setValue(this.user.company.id);
    }
    [company, name, vat].forEach(control => control?.updateValueAndValidity({ emitEvent: false }));
  }

  getUserData(): void {
    this.user_api_service.get_user_data()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.user = response.data;
          if (response.data.roles.id > Roles.MANAGEMENT_SYSTEM) {
            this.readonly = true;
          }
          this.updateCompanyValidators();
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  populateFormOnEdit(): void {
    if (this.data) {
      this.technicianForm.patchValue({
        name: this.data.name,
        lastname: this.data.lastname,
        email: this.data.email,
        role: this.data.roles?.id ?? Roles.TECHNICIAN,
        phone: this.data.phone,
        prefix: this.data.prefix?.id,
        company: this.data.company?.id
      });
    }
  }

  loadCompanies(): void {
    this.company_api_service.get_company_list()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.companies = response.data;
        },
        error: (error) => {
          console.log(error);
        }
      })
  }

  loadPrefixes(): void {
    this.prefix_api_service.get_prefixes().subscribe(
      (response) => {
        this.prefixes = response.data;
        if (!this.technicianForm.get('prefix')?.value) {
          this.setDefaultPrefix();
        }
      },
      (error) => {
        console.error('Error loading prefixes:', error);
      }
    );
  }

  filteredPrefixes(): Prefix[] {
    if (!this.prefixFilter) return this.prefixes;
    const lower = this.prefixFilter.toLowerCase();
    return this.prefixes.filter(p => p.prefix.toLowerCase().includes(lower));
  }

  filteredCompanies(): Company[] {
    if (!this.companyFilter) return this.companies;
    const lower = this.companyFilter.toLowerCase();
    return this.companies.filter(c => c.name.toLowerCase().includes(lower));
  }

  onPrefixDropdownOpened(opened: boolean): void {
    if (!opened) {
      this.prefixFilter = '';
    } else {
      setTimeout(() => {
        this.filterInputRef?.nativeElement?.focus();
      });
    }
  }

  onCompanyDropdownOpened(opened: boolean): void {
    if (!opened) {
      this.companyFilter = '';
    } else {
      setTimeout(() => {
        this.companyFilterInputRef?.nativeElement?.focus();
      });
    }
  }

  setDefaultPrefix(): void {
    const defaultPrefix = this.prefixes.find(prefix => prefix.default === true);
    if (defaultPrefix) {
      this.technicianForm.controls['prefix'].setValue(defaultPrefix.id);
    }
  }

  onPhoneInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;
    const numericValue = value.replace(/[^0-9+\- ]/g, '');
    if (value !== numericValue) {
      inputElement.value = numericValue;
      this.technicianForm.controls['phone'].setValue(numericValue);
    }
  }

  onSubmit(): void {
    if (this.technicianForm.valid) {
      if (this.data) {
        this.modifyTechnician();
      } else {
        this.addTechnician();
      }
    }
  }

  addTechnician(): void {
    this.saving = true;
    this.technician_api_service.add_technician(
      this.technicianForm.get('name')?.value,
      this.technicianForm.get('lastname')?.value,
      this.technicianForm.get('email')?.value,
      this.technicianForm.get('company')?.value,
      this.technicianForm.get('phone')?.value,
      this.technicianForm.get('prefix')?.value,
      this.technicianForm.get('role')?.value,
      this.isNewCompany ? {
        name: this.technicianForm.get('new_company_name')?.value?.trim(),
        vat: this.technicianForm.get('new_company_vat')?.value?.trim()
      } : undefined
    )
      .pipe(takeUntil(this.destroy$), finalize(() => this.saving = false))
      .subscribe({
        next: (response) => {
          if (response?.success === false) {
            this.showSaveError(response, 'technicians_page.toast_add_error');
            return;
          }
          this.dialogRef.close(response);
        },
        // Stay open with what was typed: closing with the error made the list show "Tecnico aggiunto"
        error: (error) => this.showSaveError(error, 'technicians_page.toast_add_error')
      });
  }

  private showSaveError(error: any, fallbackKey: string): void {
    const body = error?.error ?? error;
    // A 400 from the API carries the real reasons in data as [{field: {msg, code}}]; message is only "validation_error"
    const reasons = Array.isArray(body?.data)
      ? body.data.flatMap((item: any) => Object.values(item ?? {}).map((reason: any) => reason?.msg)).filter(Boolean)
        .map((reason: string) => this.translatedReason(reason))
      : [];
    const message = reasons.length ? reasons.join(', ') : (body?.message || error?.message);
    const detail = typeof message === 'string' && message && message !== 'validation_error'
      && !message.startsWith('Http failure') ? `: ${message}` : '';
    this.toastr_service.showError(this.translate.instant(fallbackKey) + detail);
  }

  // Codes the API sends for the company checks (VAT already used, company with a backoffice already)
  private translatedReason(reason: string): string {
    const key = `technicians_page.errors.${reason}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : reason;
  }

  modifyTechnician(): void {
    if (this.data?.id){
      this.saving = true;
      this.technician_api_service.update_technician(
        this.data.id,
        this.technicianForm.get('name')?.value,
        this.technicianForm.get('lastname')?.value,
        this.technicianForm.get('email')?.value,
        this.technicianForm.get('company')?.value,
        this.technicianForm.get('phone')?.value,
        this.technicianForm.get('prefix')?.value,
        this.technicianForm.get('role')?.value
      )
        .pipe(finalize(() => this.saving = false))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.dialogRef.close(response);
            } else {
              this.showSaveError(response, 'technicians_page.toast_update_error');
            }
          },
          error: (error) => this.showSaveError(error, 'technicians_page.toast_update_error')
        });
    }
  }


  get canDelete(): boolean {
    return !!this.data?.id && !!this.user?.roles && this.user.roles.id < Roles.OPERATOR_BO;
  }

  deleteTechnician(): void {
    if (!this.canDelete || !this.data?.id) {
      return;
    }

    const confirmRef = this.dialog.open(ModalsConfirmComponent, {
      data: {
        title: 'technicians.delete',
        message: 'technicians.delete_confirm',
        confirmText: 'technicians.confirm',
        cancelText: 'technicians.cancel'
      },
      width: '400px',
      autoFocus: false,
    });

    confirmRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirmed => {
        if (!confirmed || !this.data?.id) {
          return;
        }
        this.technician_api_service.delete_technician(this.data.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.dialogRef.close({ deleted: true });
            },
            error: (error) => {
              this.dialogRef.close({ deleted: false, error });
            }
          });
      });
  }

  // closeDialog(): void {
  //   this.dialogRef.close({ event: 'Cancel' });
  // }



  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected readonly Roles = Roles;
}
